/**
 * Attribution capture (spec §5.3, as amended for a static bundle: ARCHITECTURE.md "Attribution cookie").
 * Two JS-set, host-only (no Domain attribute), SameSite=Lax cookies, read by the gate on the server:
 *   kgob_ev_attr  JSON {first, last} touches with the §5.2 fields, 90 days. First touch written once
 *                 (only if absent); last touch overwritten on every landing that carries campaign
 *                 parameters or an external referrer.
 *   kgob_ev_rep   the ?rep= code, 30 days, last touch, passed through verbatim and never validated here.
 * Nothing else is ever written. Not HttpOnly (a static bundle cannot set one); the cookies hold only UTMs,
 * click ids, the landing URL and the referrer. Safari caps script-set cookies at about a week: a known
 * loss, recorded in SPEC-AMENDMENTS-PROPOSED.md.
 */
export const ATTR_COOKIE = "kgob_ev_attr";
export const REP_COOKIE = "kgob_ev_rep";
export const ATTR_MAX_AGE = 90 * 24 * 3600;
export const REP_MAX_AGE = 30 * 24 * 3600;

export const CAMPAIGN_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "gbraid", "wbraid", "fbclid", "msclkid", "li_fat_id"];
/** Everything the canonical URL must never carry (§5.3). */
export const TRACKING_PARAMS = [...CAMPAIGN_PARAMS, "rep"];

export const emptyTouch = () => Object.fromEntries([...CAMPAIGN_PARAMS, "landing_url", "referrer", "at"].map((k) => [k, null]));

function isExternalReferrer(referrer, host) {
  if (!referrer) return false;
  try { return new URL(referrer).host !== host; } catch { return false; }
}

/** Builds a touch from the page URL and referrer, or null when nothing campaign-like is present. */
export function touchFrom(href, referrer, now = new Date()) {
  const url = new URL(href);
  const get = (k) => (url.searchParams.get(k) ?? null)?.slice(0, 256) ?? null;
  const hasCampaign = CAMPAIGN_PARAMS.some((k) => url.searchParams.has(k));
  const external = isExternalReferrer(referrer, url.host);
  if (!hasCampaign && !external) return null;
  return { ...emptyTouch(), ...Object.fromEntries(CAMPAIGN_PARAMS.map((k) => [k, get(k)])), landing_url: url.toString().slice(0, 512), referrer: referrer ? referrer.slice(0, 512) : null, at: now.toISOString() };
}

export function parseCookies(cookieString) {
  const out = {};
  for (const part of (cookieString || "").split(";")) {
    const i = part.indexOf("=");
    if (i < 0) continue;
    const k = part.slice(0, i).trim();
    if (k) out[k] = part.slice(i + 1).trim();
  }
  return out;
}

export function parseAttribution(raw) {
  if (!raw) return { first: null, last: null };
  try {
    const v = JSON.parse(decodeURIComponent(raw));
    return { first: v.first ?? null, last: v.last ?? null };
  } catch { return { first: null, last: null }; }
}

/**
 * Pure: given the page URL, referrer and the current cookie string, the cookies to write (name, value,
 * maxAge) or an empty list when nothing changed.
 */
export function attributionUpdates({ href, referrer, cookies, now = new Date() }) {
  const jar = parseCookies(cookies);
  const existing = parseAttribution(jar[ATTR_COOKIE]);
  const touch = touchFrom(href, referrer, now);
  const rep = new URL(href).searchParams.get("rep")?.slice(0, 256) || null;
  const writes = [];
  if (touch) {
    const next = { first: existing.first ?? touch, last: touch };
    writes.push({ name: ATTR_COOKIE, value: encodeURIComponent(JSON.stringify(next)), maxAge: ATTR_MAX_AGE });
  }
  if (rep) writes.push({ name: REP_COOKIE, value: encodeURIComponent(rep), maxAge: REP_MAX_AGE });
  return writes;
}

/** Cookie attributes: Path=/, Max-Age, SameSite=Lax, Secure on https, NO Domain (host-only). */
export function cookieString({ name, value, maxAge }, secure) {
  return `${name}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure ? "; Secure" : ""}`;
}

/** Runs the capture against the live document. Safe to call on every landing; writes only when something changed. */
export function captureAttribution(doc = typeof document !== "undefined" ? document : null) {
  if (!doc) return [];
  const writes = attributionUpdates({ href: doc.location.href, referrer: doc.referrer, cookies: doc.cookie });
  for (const w of writes) doc.cookie = cookieString(w, doc.location.protocol === "https:");
  return writes;
}
